import React from 'react';

import { Box, Paper, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { useGLMCardStyles } from '../styles';
import { ACTION_LABELS, FormValues, KiloGLMFlowActions, KiloGLMFlowTriggers } from '../types';

const ACTION_DESCRIPTIONS: Partial<Record<KiloGLMFlowActions, string>> = {
  [KiloGLMFlowActions.ADD_TAGS]: "Add one or more tags to the athlete's contact in Gym Lead Machine.",
  [KiloGLMFlowActions.REMOVE_TAGS]:
    "Remove one or more tags from the athlete's contact in Gym Lead Machine.",
  [KiloGLMFlowActions.LINK_CONTACT_AND_ADD_TAG]:
    'Link the athlete to a GLM contact (creating it if needed), then add the chosen tag(s).',
  [KiloGLMFlowActions.LINK_CONTACT_AND_MOVE_PIPELINE_STAGE]:
    'Link the athlete to a GLM contact (creating it if needed), then move it to the chosen pipeline stage.',
};

// Per-trigger valid actions (mirrors the backend registry / DEV-2788 taxonomy).
const ACTIONS_BY_TRIGGER: Partial<Record<KiloGLMFlowTriggers, KiloGLMFlowActions[]>> = {
  [KiloGLMFlowTriggers.ATHLETE_CREATED]: [
    KiloGLMFlowActions.LINK_CONTACT_AND_ADD_TAG,
    KiloGLMFlowActions.LINK_CONTACT_AND_MOVE_PIPELINE_STAGE,
  ],
  [KiloGLMFlowTriggers.PACKAGE_STARTED]: [KiloGLMFlowActions.ADD_TAGS, KiloGLMFlowActions.REMOVE_TAGS],
  [KiloGLMFlowTriggers.PACKAGE_PURCHASED]: [KiloGLMFlowActions.ADD_TAGS, KiloGLMFlowActions.REMOVE_TAGS],
  [KiloGLMFlowTriggers.PACKAGE_ENDED]: [KiloGLMFlowActions.ADD_TAGS, KiloGLMFlowActions.REMOVE_TAGS],
  [KiloGLMFlowTriggers.PACKAGE_CANCELLED]: [KiloGLMFlowActions.ADD_TAGS, KiloGLMFlowActions.REMOVE_TAGS],
  [KiloGLMFlowTriggers.PACKAGE_HOLD_START]: [KiloGLMFlowActions.ADD_TAGS, KiloGLMFlowActions.REMOVE_TAGS],
  [KiloGLMFlowTriggers.PACKAGE_HOLD_END]: [KiloGLMFlowActions.ADD_TAGS, KiloGLMFlowActions.REMOVE_TAGS],
  [KiloGLMFlowTriggers.ATHLETE_STATUS_CHANGED]: [
    KiloGLMFlowActions.ADD_TAGS,
    KiloGLMFlowActions.REMOVE_TAGS,
  ],
  [KiloGLMFlowTriggers.CLASS_CHECKIN]: [KiloGLMFlowActions.ADD_TAGS, KiloGLMFlowActions.REMOVE_TAGS],
  [KiloGLMFlowTriggers.APPPOINTMENT_CHECKIN]: [KiloGLMFlowActions.ADD_TAGS, KiloGLMFlowActions.REMOVE_TAGS],
  [KiloGLMFlowTriggers.ATHLETE_CHECKINS_REACHED]: [
    KiloGLMFlowActions.ADD_TAGS,
    KiloGLMFlowActions.REMOVE_TAGS,
  ],
};

interface IKiloGLMActionSelectProps {
  handleActionSelect: (value: string) => void;
  form: UseFormReturnType<FormValues, (values: FormValues) => FormValues>;
}

const KiloGLMActionSelect = ({ handleActionSelect, form }: IKiloGLMActionSelectProps): JSX.Element => {
  const { classes, cx } = useGLMCardStyles();

  const visibleActions = ACTIONS_BY_TRIGGER[form.values.trigger as KiloGLMFlowTriggers] ?? [];

  return (
    <Paper>
      <Stack sx={{ paddingTop: '20px' }}>
        <Title order={3}>What should happen in GLM?</Title>
        <Text size={15}>Select the action to perform in Gym Lead Machine when the trigger occurs.</Text>
        <Stack spacing={6}>
          {visibleActions.map(action => {
            const isSelected = form.values.action === action;
            return (
              <UnstyledButton key={action} onClick={() => handleActionSelect(action)}>
                <Box className={cx(classes.card, { [classes.cardSelected]: isSelected })}>
                  <Text weight={isSelected ? 600 : 400}>{ACTION_LABELS[action]}</Text>
                  {isSelected && ACTION_DESCRIPTIONS[action] && (
                    <Text size='sm' color='#6B6B6B' mt={4}>
                      {ACTION_DESCRIPTIONS[action]}
                    </Text>
                  )}
                </Box>
              </UnstyledButton>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default KiloGLMActionSelect;
